import ReactNativeBlobUtil from 'react-native-blob-util';
import { CryptEngine } from './CryptEngine';
import { getUid } from '../store/token';

// 下载任务的状态枚举
export enum DownloadTaskState {
    PENDING = 'pending',      // 等待中
    DOWNLOADING = 'downloading', // 下载中
    PAUSED = 'paused',        // 已暂停
    COMPLETED = 'completed',   // 已完成
    FAILED = 'failed',        // 失败
    CANCELLED = 'cancelled',  // 已取消
}

// 处理重复任务的策略
export enum DuplicateTaskStrategy {
    SKIP = 'skip',            // 跳过，返回已存在的任务ID
    REPLACE = 'replace',      // 替换，取消旧任务，创建新任务
    RETRY = 'retry',          // 重试，如果旧任务失败，创建新任务
}

// 下载事件类型
export enum DownloadEventType {
    TASK_ADDED = 'task_added',
    TASK_STARTED = 'task_started',
    TASK_PROGRESS = 'task_progress',
    TASK_PAUSED = 'task_paused',
    TASK_RESUMED = 'task_resumed',
    TASK_COMPLETED = 'task_completed',
    TASK_FAILED = 'task_failed',
    TASK_CANCELLED = 'task_cancelled',
    QUEUE_CHANGED = 'queue_changed',
}

// 下载进度信息
export interface DownloadProgress {
    taskId: string;
    progress: number;        // 0-100 的百分比
    loadedSize: number;      // 已下载大小（字节）
    totalSize: number;       // 总大小（字节）
    speed?: number;          // 下载速度（字节/秒）
    estimatedTimeRemaining?: number; // 剩余时间（秒）
}

// 下载事件对象
export interface DownloadEvent {
    type: DownloadEventType;
    taskId: string;
    timestamp: number;
    data?: any;
}

// 下载任务配置
export interface DownloadTaskConfig {
    maxRetries?: number;      // 最大重试次数，默认3次
    retryDelay?: number;      // 重试延迟（毫秒），默认1000ms
}

// 下载完成回调
export type DownloadCompleteCallback = (result: DownloadResult) => void;

// 下载进度回调
export type DownloadProgressCallback = (progress: DownloadProgress) => void;

// 下载错误回调
export type DownloadErrorCallback = (taskId: string, error: Error) => void;

// 下载进度回调类型（用于下载函数内部调用）
export type DownloadCallbacks = {
    onProgress: DownloadProgressCallback;
};

// 下载函数类型
export type DownloadFunctionType = (callbacks: DownloadCallbacks) => Promise<any>;
export interface DownloadResult {
    taskId: string;
    success: boolean;
    data?: any;
    error?: Error;
}

// 下载任务类
class DownloadTask {
    public id: string;
    public url: string;
    public state: DownloadTaskState = DownloadTaskState.PENDING;
    public progress: number = 0;
    public loadedSize: number = 0;
    public totalSize: number = 0;
    public startTime: number = 0;
    public endTime: number = 0;
    public retryCount: number = 0;
    public maxRetries: number = 3;
    public retryDelay: number = 1000;

    private onProgress: DownloadProgressCallback | null = null;
    private onComplete: DownloadCompleteCallback | null = null;
    private onError: DownloadErrorCallback | null = null;
    private onEvent: ((event: DownloadEvent) => void) | null = null;
    private downloadFn: DownloadFunctionType;
    private cancelled: boolean = false;
    private paused: boolean = false;
    private lastProgressTime: number = Date.now();
    private lastProgressSize: number = 0;
    private lastResult: DownloadResult | null = null;
    private lastError: Error | null = null;

    constructor(
        id: string,
        url: string,
        downloadFn: DownloadFunctionType,
        config?: DownloadTaskConfig
    ) {
        this.id = id;
        this.url = url;
        this.downloadFn = downloadFn;
        if (config) {
            this.maxRetries = config.maxRetries ?? 3;
            this.retryDelay = config.retryDelay ?? 1000;
        }
    }

    // 设置进度回调
    public setProgressCallback(callback: DownloadProgressCallback) {
        this.onProgress = callback;
    }

    // 设置完成回调
    public setCompleteCallback(callback: DownloadCompleteCallback) {
        this.onComplete = callback;
    }

    // 设置错误回调
    public setErrorCallback(callback: DownloadErrorCallback) {
        this.onError = callback;
    }

    // 设置事件回调
    public setEventCallback(callback: (event: DownloadEvent) => void) {
        this.onEvent = callback;
    }

    // 执行下载任务
    public async execute(): Promise<DownloadResult> {
        this.state = DownloadTaskState.DOWNLOADING;
        this.startTime = Date.now();
        this.emitEvent(DownloadEventType.TASK_STARTED);

        try {
            // 创建进度回调 wrapper
            const progressCallback: DownloadProgressCallback = (progress: DownloadProgress) => {
                // 检查是否暂停
                while (this.paused && !this.cancelled) {
                    // 等待恢复
                }

                if (this.cancelled) {
                    throw new Error('Download cancelled');
                }

                // 计算下载速度
                const currentTime = Date.now();
                const timeDiff = (currentTime - this.lastProgressTime) / 1000; // 转换为秒
                const sizeDiff = progress.loadedSize - this.lastProgressSize;

                let speed = 0;
                if (timeDiff > 0) {
                    speed = sizeDiff / timeDiff; // 字节/秒
                }

                this.progress = progress.progress;
                this.loadedSize = progress.loadedSize;
                this.totalSize = progress.totalSize;
                this.lastProgressTime = currentTime;
                this.lastProgressSize = progress.loadedSize;

                // 计算剩余时间
                let estimatedTimeRemaining = undefined;
                if (speed > 0 && progress.totalSize > 0) {
                    const remainingBytes = progress.totalSize - progress.loadedSize;
                    estimatedTimeRemaining = remainingBytes / speed;
                }

                const progressWithSpeed: DownloadProgress = {
                    ...progress,
                    speed,
                    estimatedTimeRemaining,
                };

                if (this.onProgress) {
                    this.onProgress(progressWithSpeed);
                }

                this.emitEvent(DownloadEventType.TASK_PROGRESS, progressWithSpeed);
            };

            const result = await this.downloadFn({
                onProgress: progressCallback,
            });

            this.state = DownloadTaskState.COMPLETED;
            this.endTime = Date.now();
            this.progress = 100;

            if (this.onProgress) {
                this.onProgress({
                    taskId: this.id,
                    progress: 100,
                    loadedSize: this.totalSize,
                    totalSize: this.totalSize,
                    speed: 0,
                });
            }

            const downloadResult: DownloadResult = {
                taskId: this.id,
                success: true,
                data: result,
            };

            // 保存完成结果，以便后续查询
            this.lastResult = downloadResult;

            // 将下载成功标记存储到本地
            if (typeof (global as any).downloadPersist === 'function') {
                const curUid = getUid() || '';
                (global as any).downloadPersist(`download_result_${this.id}_${curUid}`, null);
            }

            if (this.onComplete) {
                this.onComplete(downloadResult);
            }

            this.emitEvent(DownloadEventType.TASK_COMPLETED, downloadResult);

            return downloadResult;
        } catch (error) {
            if (error instanceof Error && error.message === 'Download cancelled') {
                this.state = DownloadTaskState.CANCELLED;
                this.emitEvent(DownloadEventType.TASK_CANCELLED);
            } else {
                // 尝试重试
                if (this.retryCount < this.maxRetries) {
                    this.retryCount++;
                    console.log(`下载失败，准备重试 (${this.retryCount}/${this.maxRetries})...`);

                    // 等待后重试
                    await new Promise((resolve) => setTimeout(resolve, this.retryDelay));

                    // 重置进度
                    this.progress = 0;
                    this.loadedSize = 0;
                    this.lastProgressSize = 0;

                    return this.execute();
                }

                this.state = DownloadTaskState.FAILED;
                this.emitEvent(DownloadEventType.TASK_FAILED);
            }

            this.endTime = Date.now();

            const err = error instanceof Error ? error : new Error(String(error));

            // 保存错误信息，以便后续查询
            this.lastError = err;
            // 将下载失败标记存储到本地
            if (typeof (global as any).downloadPersist === 'function') {
                const curUid = getUid() || '';
                (global as any).downloadPersist(`download_result_${this.id}_${curUid}`, 'false');
            }

            if (this.onError) {
                this.onError(this.id, err);
            }

            
            return {
                taskId: this.id,
                success: false,
                error: err,
            };
        }
    }

    // 暂停下载
    public pause() {
        if (this.state === DownloadTaskState.DOWNLOADING) {
            this.paused = true;
            this.state = DownloadTaskState.PAUSED;
            this.emitEvent(DownloadEventType.TASK_PAUSED);
        }
    }

    // 恢复下载
    public resume() {
        if (this.state === DownloadTaskState.PAUSED) {
            this.paused = false;
            this.state = DownloadTaskState.DOWNLOADING;
            this.emitEvent(DownloadEventType.TASK_RESUMED);
        }
    }

    // 取消下载
    public cancel() {
        this.cancelled = true;
        this.paused = false;
        this.state = DownloadTaskState.CANCELLED;
        this.emitEvent(DownloadEventType.TASK_CANCELLED);
    }

    // 检查是否被取消
    public isCancelled(): boolean {
        return this.cancelled;
    }

    // 检查是否暂停
    public isPaused(): boolean {
        return this.paused;
    }

    // 发送事件
    private emitEvent(type: DownloadEventType, data?: any) {
        if (this.onEvent) {
            this.onEvent({
                type,
                taskId: this.id,
                timestamp: Date.now(),
                data,
            });
        }
    }

    // 获取任务的最后结果（仅当任务已完成时有），否则返回 null
    public getLastResult(): DownloadResult | null {
        return this.lastResult;
    }

    // 获取任务的最后错误（仅当任务已失败时有效），否则返回 null
    public getLastError(): Error | null {
        return this.lastError;
    }

    // 获取任务信息
    public getInfo() {
        return {
            id: this.id,
            url: this.url,
            state: this.state,
            progress: this.progress,
            loadedSize: this.loadedSize,
            totalSize: this.totalSize,
            startTime: this.startTime,
            endTime: this.endTime,
            retryCount: this.retryCount,
            maxRetries: this.maxRetries,
        };
    }
}

// 下载队列类
class DownloadQueue {
    private maxConcurrentDownloads: number;
    private queue: (() => Promise<any>)[] = [];
    private activeDownloads: number = 0;

    constructor(maxConcurrentDownloads: number = 3) {
        this.maxConcurrentDownloads = maxConcurrentDownloads;
    }

    // 添加任务到队列
    public addTask(task: () => Promise<any>) {
        this.queue.push(task);
        this.runNext(); // 尝试开始下载下一个任务
    }

    // 运行下一个任务
    private async runNext() {
        if (this.activeDownloads < this.maxConcurrentDownloads && this.queue.length > 0) {
            const task = this.queue.shift();
            if (task) {
                this.activeDownloads++; // 增加当前下载数
                try {
                    await task();
                } catch (error) {
                    console.error('下载失败:', error);
                } finally {
                    this.activeDownloads--; // 下载完成后减少当前下载数
                    this.runNext(); // 尝试下载下一个任务
                }
            }
        }
    }

    // 获取当前活跃下载数
    public getActiveDownloadsCount(): number {
        return this.activeDownloads;
    }

    // 获取队列中的任务数
    public getQueueLength(): number {
        return this.queue.length;
    }
}

// 下载管理器类
export class DownloadManager {
    private downloadQueue: DownloadQueue;
    private tasks: Map<string, DownloadTask> = new Map();
    private completedTasks: Set<string> = new Set();
    private eventListeners: Map<DownloadEventType, Set<(event: DownloadEvent) => void>> = new Map();
    private persistenceEnabled: boolean = false;
    private persistenceKey: string = 'download_tasks_state';

    constructor(maxConcurrent: number = 3, enablePersistence: boolean = false) {
        this.downloadQueue = new DownloadQueue(maxConcurrent);
        this.persistenceEnabled = enablePersistence;

        // 初始化事件监听器字典
        Object.values(DownloadEventType).forEach((type) => {
            this.eventListeners.set(type, new Set());
        });
    }

    /**
     * 监听下载事件
     */
    public on(eventType: DownloadEventType, callback: (event: DownloadEvent) => void) {
        const listeners = this.eventListeners.get(eventType);
        if (listeners) {
            listeners.add(callback);
        }
    }

    /**
     * 取消监听下载事件
     */
    public off(eventType: DownloadEventType, callback: (event: DownloadEvent) => void) {
        const listeners = this.eventListeners.get(eventType);
        if (listeners) {
            listeners.delete(callback);
        }
    }

    /**
     * 发送事件到所有监听者
     */
    private emitEvent(event: DownloadEvent) {
        const listeners = this.eventListeners.get(event.type);
        if (listeners) {
            listeners.forEach((callback) => {
                try {
                    callback(event);
                } catch (error) {
                    console.error(`事件回调执行出错: ${error}`);
                }
            });
        }
        // 然后保存状态到本地存储
        if (this.persistenceEnabled) {
            this.persistTasksState();
        }
    }

    /**
     * 将任务状态保存到本地存储（异步）
     */
    private async persistTasksState() {
        try {
            // 这里需要导入 AsyncStorage，但由于不想增加依赖，
            // 我们提供一个接口供外部实现
            if (typeof (global as any).downloadPersist === 'function') {
                const state = Array.from(this.tasks.values()).map((task) => task.getInfo());
                await (global as any).downloadPersist(this.persistenceKey, state);
            }
        } catch (error) {
            console.error('保存任务状态失败:', error);
        }
    }

    /**
     * 从本地存储恢复任务状态
     */
    public async restoreTasksState() {
        try {
            if (typeof (global as any).downloadRestore === 'function') {
                const state = await (global as any).downloadRestore(this.persistenceKey);
                console.log('恢复下载任务状态:', state);
                // 这里可以根据保存的状态重新创建任务
            }
        } catch (error) {
            console.error('恢复任务状态失败:', error);
        }
    }

    /**
     * 添加下载任务
     * @param id 任务唯一标识符
     * @param url 下载 URL
     * @param downloadFn 下载函数，接收进度回调
     * @param onProgress 进度回调
     * @param onComplete 完成回调
     * @param onError 错误回调
     * @param strategy 处理重复任务的策略
     * @param config 任务配置（重试次数、重试延迟等）
     */
    public addDownloadTask(
        id: string,
        url: string,
        downloadFn: DownloadFunctionType,
        onProgress?: DownloadProgressCallback,
        onComplete?: DownloadCompleteCallback,
        onError?: DownloadErrorCallback,
        strategy: DuplicateTaskStrategy = DuplicateTaskStrategy.SKIP,
        config?: DownloadTaskConfig
    ): string {
        if (this.tasks.has(id)) {
            const existingTask = this.tasks.get(id)!;
            const taskState = existingTask.getInfo().state;

            switch (strategy) {
                case DuplicateTaskStrategy.SKIP:
                    console.warn(`任务 ${id} 已存在，跳过添加，但更新回调`, taskState);

                    // 如果任务已完成，立即触发完成回调
                    if (taskState === DownloadTaskState.COMPLETED) {
                        if (onComplete) {
                            const lastResult = existingTask.getLastResult();
                            if (lastResult) {
                                onComplete(lastResult);
                            }
                        }
                        return id;
                    }

                    // 如果任务已失败或取消，立即触发错误回调
                    if (
                        taskState === DownloadTaskState.FAILED ||
                        taskState === DownloadTaskState.CANCELLED
                    ) {
                        console.log(`任务 ${id} 已失败，准备重新下载...`);
                        this.tasks.delete(id);
                        this.completedTasks.delete(id);
                        return id;
                    }

                    // 重要：即使跳过添加，也要更新回调，以便新页面能接收进度
                    this.updateTaskCallbacks(id, onProgress, onComplete, onError);
                    return id;

                case DuplicateTaskStrategy.REPLACE:
                    console.log(`任务 ${id} 已存在，准备替换...`);
                    existingTask.cancel();
                    this.tasks.delete(id);
                    this.completedTasks.delete(id);
                    break;

                case DuplicateTaskStrategy.RETRY:
                    // 如果任务已完成，立即触发完成回调
                    if (taskState === DownloadTaskState.COMPLETED) {
                        if (onComplete) {
                            const lastResult = existingTask.getLastResult();
                            if (lastResult) {
                                onComplete(lastResult);
                            }
                        }
                        return id;
                    }

                    if (
                        taskState === DownloadTaskState.FAILED ||
                        taskState === DownloadTaskState.CANCELLED
                    ) {
                        console.log(`任务 ${id} 已失败，准备重新下载...`);
                        this.tasks.delete(id);
                        this.completedTasks.delete(id);
                    } else {
                        // 重要：即使跳过添加，也要更新回调，以便新页面能接收进度
                        this.updateTaskCallbacks(id, onProgress, onComplete, onError);
                        return id;
                    }
                    break;
            }
        }

        const task = new DownloadTask(id, url, downloadFn, config);

        if (onProgress) {
            task.setProgressCallback(onProgress);
        }
        if (onComplete) {
            task.setCompleteCallback(onComplete);
        }
        if (onError) {
            task.setErrorCallback(onError);
        }

        // 设置事件回调
        task.setEventCallback((event) => {
            this.emitEvent(event);
        });

        this.tasks.set(id, task);

        // 发送任务添加事件
        this.emitEvent({
            type: DownloadEventType.TASK_ADDED,
            taskId: id,
            timestamp: Date.now(),
            data: { url },
        });

        // 将任务加入队列
        this.downloadQueue.addTask(async () => {
            const result = await task.execute();
            if (result.success) {
                this.tasks.delete(id);
                this.completedTasks.add(id);
            }

            this.emitEvent({
                type: DownloadEventType.QUEUE_CHANGED,
                taskId: '',
                timestamp: Date.now(),
                data: {
                    activeCount: this.downloadQueue.getActiveDownloadsCount(),
                    queueLength: this.downloadQueue.getQueueLength(),
                },
            });
        });

        return id;
    }

    /**
     * 更新已存在任务的回调
     * 用于在任务已存在的情况下，向其添加或更新回调函数
     * @param taskId 任务 ID
     * @param onProgress 进度回调（可选）
     * @param onComplete 完成回调（可选）
     * @param onError 错误回调（可选）
     * @returns 是否更新成功
     */
    public updateTaskCallbacks(
        taskId: string,
        onProgress?: DownloadProgressCallback,
        onComplete?: DownloadCompleteCallback,
        onError?: DownloadErrorCallback
    ): boolean {
        const task = this.tasks.get(taskId);
        if (!task) {
            console.warn(`任务 ${taskId} 不存在`);
            return false;
        }

        if (onProgress) {
            task.setProgressCallback(onProgress);
        }
        if (onComplete) {
            task.setCompleteCallback(onComplete);
        }
        if (onError) {
            task.setErrorCallback(onError);
        }

        return true;
    }

    /**
     * 获取任务进度信息
     */
    public getTaskProgress(taskId: string): DownloadProgress | null {
        const task = this.tasks.get(taskId);
        if (!task) {
            return null;
        }

        return {
            taskId: task.id,
            progress: task.progress,
            loadedSize: task.loadedSize,
            totalSize: task.totalSize,
        };
    }

    /**
     * 获取任务状态
     */
    public getTaskState(taskId: string): DownloadTaskState | null {
        const task = this.tasks.get(taskId);
        return task ? task.state : null;
    }

    /**
     * 获取任务完整信息
     */
    public getTaskInfo(taskId: string) {
        const task = this.tasks.get(taskId);
        return task ? task.getInfo() : null;
    }

    /**
     * 暂停下载任务
     */
    public pauseTask(taskId: string) {
        const task = this.tasks.get(taskId);
        if (task) {
            task.pause();
        }
    }

    /**
     * 恢复下载任务
     */
    public resumeTask(taskId: string) {
        const task = this.tasks.get(taskId);
        if (task) {
            task.resume();
        }
    }

    /**
     * 取消指定任务
     */
    public cancelTask(taskId: string) {
        const task = this.tasks.get(taskId);
        if (task) {
            task.cancel();
        }
    }

    /**
     * 获取所有任务信息
     */
    public getAllTasks() {
        const result = Array.from(this.tasks.values()).map((task) => task.getInfo());
        return result;
    }

    /**
     * 按状态筛选任务
     */
    public getTasksByState(state: DownloadTaskState) {
        return Array.from(this.tasks.values())
            .filter((task) => task.getInfo().state === state)
            .map((task) => task.getInfo());
    }

    /**
     * 获取已完成的任务
     */
    public getCompletedTasks() {
        return Array.from(this.completedTasks);
    }

    /**
     * 获取活跃的下载数量
     */
    public getActiveDownloadCount(): number {
        return this.downloadQueue.getActiveDownloadsCount();
    }

    /**
     * 获取队列中的任务数量
     */
    public getQueueLength(): number {
        return this.downloadQueue.getQueueLength();
    }

    /**
     * 移除已完成的任务
     */
    public removeTask(taskId: string) {
        this.tasks.delete(taskId);
        this.completedTasks.delete(taskId);
    }

    /**
     * 清空所有任务
     */
    public clearAll() {
        this.tasks.forEach((task) => {
            task.cancel();
        });
        this.tasks.clear();
        this.completedTasks.clear();
    }

    /**
     * 根据下载URL查询是否下载成功
     * @param url 下载的远程URL
     * @returns Promise<'success' | 'failed' | 'unknown'>
     *          - 'success': 下载成功过
     *          - 'failed': 下载失败过
     *          - 'unknown': 未知状态或尚未下载过
     */
    public async isDownloadSuccessful(url: string): Promise<'success' | 'failed' | 'unknown'> {
        try {
            const taskId = `doc_${CryptEngine.md5(url)}`;

            if (typeof (global as any).downloadRestore === 'function') {
                const curUid = getUid() || '';
                const result = await (global as any).downloadRestore(`download_result_${taskId}_${curUid}`);

                if (result === 'true') {
                    return 'success';
                } else if (result === 'false') {
                    return 'failed';
                }
            }

            return 'unknown';
        } catch (error) {
            console.error('查询下载状态失败:', error);
            return 'unknown';
        }
    }

    /**
     * 清除某个URL的下载成功/失败标记
     * @param url 下载的远程URL
     */
    public async clearDownloadMark(url: string): Promise<void> {
        try {
            const taskId = `doc_${CryptEngine.md5(url)}`;

            if (typeof (global as any).downloadPersist === 'function') {
                const curUid = getUid() || '';
                // 使用null表示删除标记
                (global as any).downloadPersist(`download_result_${taskId}_${curUid}`, null);
            }
        } catch (error) {
            console.error('清除下载标记失败:', error);
        }
    }

    /**
     * 根据URL获取下载状态详情
     * @param url 下载的远程URL
     * @returns Promise 返回包含状态信息的对象
     */
    public async getDownloadStatusByUrl(url: string): Promise<{
        url: string;
        status: 'success' | 'failed' | 'unknown';
        taskId: string;
    }> {
        try {
            const taskId = `doc_${CryptEngine.md5(url)}`;
            const status = await this.isDownloadSuccessful(url);

            return {
                url,
                status,
                taskId,
            };
        } catch (error) {
            console.error('获取下载状态失败:', error);
            return {
                url,
                status: 'unknown',
                taskId: '',
            };
        }
    }
}

// 导出全局下载队列实例
export const downloadQueue = new DownloadQueue(3);

// 导出全局下载管理器实例（启用持久化）
export const downloadManager = new DownloadManager(3, true);

/**
 * 配置 AsyncStorage 持久化支持
 * 在应用启动时调用此函数，以启用下载任务的持久化功能
 * 
 * 示例：
 * import AsyncStorage from '@react-native-async-storage/async-storage';
 * import { configureDownloadPersistence } from './DownloadQueue';
 * 
 * configureDownloadPersistence(AsyncStorage);
 */
export async function configureDownloadPersistence(asyncStorage: any) {
    if (!asyncStorage) {
        console.warn('AsyncStorage 未提供，持久化功能禁用');
        return;
    }

    // 设置全局持久化函数
    (global as any).downloadPersist = async (key: string, state: any) => {
        try {
            await asyncStorage.setItem(key, JSON.stringify(state));
        } catch (error) {
            console.error('AsyncStorage 保存失败:', error);
        }
    };

    (global as any).downloadRestore = async (key: string) => {
        try {
            const data = await asyncStorage.getItem(key);
            return data ? JSON.parse(data) : null;
        } catch (error) {
            console.error('AsyncStorage 恢复失败:', error);
            return null;
        }
    };

    // 恢复保存的任务状态
    await downloadManager.restoreTasksState();
}

/**
 * 创建文档下载函数
 * 这是一个工厂函数，用于创建符合 DownloadFunctionType 的下载函数
 * 
 * @param remoteUrl 远程文件URL
 * @param targetPath 本地保存路径
 * @returns DownloadFunctionType 返回可被downloadManager使用的下载函数
 * 
 * 示例用法：
 * const downloadFn = createDocumentDownloadFunction(remoteUrl, targetPath);
 * downloadManager.addDownloadTask('doc-123', remoteUrl, downloadFn, onProgress, onComplete, onError);
 */
export function createDocumentDownloadFunction(
    remoteUrl: string,
    targetPath: string
): DownloadFunctionType {
    return async (callbacks: DownloadCallbacks) => {
        let lastProgress = 0;
        let totalSize = 0;

        return ReactNativeBlobUtil.config({
            fileCache: true,
            path: targetPath,
        })
            .fetch('GET', remoteUrl)
            .progress((received: any, total: any) => {
                const receivedNum = Number(received);
                const totalNum = Number(total);
                const progress = Math.floor((receivedNum / totalNum) * 100);
                totalSize = totalNum;
                if (progress !== lastProgress) {
                    lastProgress = progress;
                    callbacks.onProgress({
                        taskId: '', // taskId会在下载管理器中设置
                        progress,
                        loadedSize: receivedNum,
                        totalSize: totalNum,
                    });
                }
            })
            .then((res: any) => ({
                path: res.path(),
                totalSize,
                success: true,
            }))
            .catch((error: any) => {
                throw new Error(`下载失败: ${error.message}`);
            });
    };
}

/**
 * 文档下载任务配置接口
 */
export interface DocumentDownloadConfig {
    remoteUrl: string;           // 远程URL
    destinationDir: string;      // 保存目录
    fileName?: string;           // 自定义文件名（可选，不指定则自动生成）
    onProgress?: DownloadProgressCallback;  // 进度回调
    onComplete?: DownloadCompleteCallback;  // 完成回调
    onError?: DownloadErrorCallback;        // 错误回调
    duplicateStrategy?: DuplicateTaskStrategy; // 重复任务策略
}

/**
 * 创建并启动文档下载任务
 * 这是一个高级API，将文件生成、目录创建、下载函数创建等逻辑集成在一起
 * 
 * @param config 下载配置
 * @returns Promise<string> 返回任务ID
 * 
 * 示例用法：
 * const taskId = await downloadDocument({
 *     remoteUrl: 'https://example.com/file.pdf',
 *     destinationDir: `${customCachePath}document`,
 *     onProgress: (progress) => console.log(`进度: ${progress.progress}%`),
 *     onComplete: (result) => console.log('完成'),
 *     onError: (taskId, error) => console.error(error),
 * });
 */
export async function downloadDocument(config: DocumentDownloadConfig): Promise<string> {
    const {
        remoteUrl,
        destinationDir,
        fileName,
        onProgress,
        onComplete,
        onError,
        duplicateStrategy = DuplicateTaskStrategy.REPLACE,
    } = config;

    // 确保目录存在
    const dirExists = await require('react-native-fs').exists(destinationDir);
    if (!dirExists) {
        await require('react-native-fs').mkdir(destinationDir);
    }

    // 生成文件名和路径
    let finalFileName = fileName;
    if (!finalFileName) {
        // 自动生成文件名：从URL中提取或使用时间戳
        const urlParts = remoteUrl.split('/');
        const urlFileName = urlParts[urlParts.length - 1]?.split('?')[0];
        const timestamp = Date.now();
        const randomNum = Math.floor(Math.random() * 10000);
        const extension = urlFileName?.split('.').pop() || 'unknown';
        finalFileName = `doc_${timestamp}_${randomNum}.${extension}`;
    }

    const targetPath = `${destinationDir}/${finalFileName}`;
    const taskId = `doc_${CryptEngine.md5(remoteUrl)}`;

    // 创建下载函数
    const downloadFn = createDocumentDownloadFunction(remoteUrl, targetPath);

    // 添加下载任务
    downloadManager.addDownloadTask(
        taskId,
        remoteUrl,
        downloadFn,
        onProgress,
        onComplete,
        onError,
        duplicateStrategy
    );

    return taskId;
}

